// Archiving and restoring a patient on the clinician dashboard.
//
// These are STATE CHANGES, which is why they matter beyond their own counts:
// whether a patient is archived is part of the current picture of the caseload,
// and the warehouse can only reconstruct that from events if every change
// announces itself. Without them, knowing today's active caseload means reading
// Firestore.
//
// Emitted from the browser rather than bridged from the server, because the
// archive and restore writes are made directly from the dashboard - there is no
// callable and no server-side writer to hang a bridge on. Both sites write an
// append-only entry to the patient's `archive_events` subcollection and then emit
// here, after the write is acknowledged, so an event never claims a change that
// did not land.
//
// The reason is a value the clinician SELECTED from a fixed list, so it travels.
// The note is free text they typed, so only its presence does - that split is
// already what the registry declares, and it is the whole reason the note is
// never read here.

import { emitAnalyticsEvent } from './emitEvent';
import { MAX_STRING_LEN } from './eventRegistry';

/** Never let an analytics failure surface in a dashboard action. */
function safeEmit(fn) {
  try {
    return fn();
  } catch (error) {
    return false;
  }
}

/**
 * A patient was archived off the active caseload.
 *
 * @param {object} opts
 * @param {string} opts.patientId
 * @param {string} [opts.reason] - The selected reason, from the dialog's fixed list.
 * @param {string} [opts.note] - Read for its presence only; never carried.
 */
export function emitPatientArchived({ patientId, reason, note } = {}) {
  return safeEmit(() => {
    if (!patientId) return false;
    const payload = {
      patient_id: String(patientId),
      // A boolean, not the text. The note is where a clinician explains a
      // clinical or safeguarding circumstance in their own words, and that
      // belongs in the record rather than the warehouse.
      has_note: Boolean(typeof note === 'string' ? note.trim() : note),
    };
    if (reason) payload.reason = String(reason).slice(0, MAX_STRING_LEN);
    return emitAnalyticsEvent('assessment_patient_archived', payload);
  });
}

/**
 * An archived patient was restored to the active caseload.
 *
 * Carries no reason because the restore path records none - it writes an entry
 * with a null reason and a null note. Declaring fields nothing populates would
 * read as coverage that does not exist.
 *
 * @param {object} opts
 * @param {string} opts.patientId
 */
export function emitPatientRestored({ patientId } = {}) {
  return safeEmit(() => {
    if (!patientId) return false;
    return emitAnalyticsEvent('assessment_patient_restored', {
      patient_id: String(patientId),
    });
  });
}
